import React, { useEffect, useRef, useState } from "react"
import styled from "@emotion/styled"
import { Box, Flex } from "@rebass/grid/emotion"

import theme from "@nutrafol/nutrafol-ui-kit/styles/theme"

import MuteIcon from "../../../../assets/icons/nova/sound-mute.svg"
import UnMuteIcon from "../../../../assets/icons/nova/sound-min.svg"
import { pauseAllVideos } from "../../../../utils/general"
import { segmentEvent } from "@nutrafol/nutrafol-ui-kit/utils/segmentfunctions"

import PlayIcon from "../../../../assets/icons/nova/play-big.svg"

const Wrapper = styled(Flex)`
  width: 100%;

  .video-wrap {
    position: relative;
    cursor: pointer;
    width: 100% !important;

    video {
      border-radius: 4px;
      width: 100%;
      height: auto;
    }
  }

  .video-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-size: 100% auto;

    .mute-unmute-wrapper {
      z-index: 2;
      height: 30px;
      width: 30px;
      background: #fff;
      border-radius: 4px;
      position: absolute;
      top: 22px;
      left: 22px;

      @media (min-width: 768px) {
        height: 32px;
        width: 32px;
        top: 24px;
        left: 24px;
      }

      svg {
        height: 30px;
        width: 30px;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        @media (min-width: 768px) {
          height: 32px;
          width: 32px;
        }
      }

      &.active {
        /* display: none; */
      }
    }

    .play-pause-wrapper {
      z-index: 1;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-size: 100% auto;

      display: flex;
      justify-content: center;
      align-items: center;

      .play-icon-svg {
        position: relative;
        padding: 10px;
        background-color: ${theme.wild.color.nova.base.white};
        border-radius: 100%;
        svg {
          width: 23px;
          height: 23px;
        }
      }
    }
  }
`

const VideoControls = styled.div``

const VideoTile = ({
  video,
  image,
  id,
  hideMuteIcon = false,
  showPlayIcon = false,
  hasSound = true,
  preload = `none`,
  name = `Video`,
}) => {
  const autoPlay = preload === `auto`

  /*
   * readyState POC for flexible control and possible loading spinner
   * const [readyState, setReadyState] = useState(0)
   */
  const [playing, setPlaying] = useState(autoPlay)
  const [muted, setMuted] = useState(hideMuteIcon === true ? false : true)
  const [posterLoaded, setPosterLoaded] = useState(false)
  const videoPlayer = useRef(null)

  const playVideo = () => {
    pauseAllVideos()
    const promise = videoPlayer?.current?.play()

    if (promise) {
      promise
        .then((_) => {
          setPlaying(true)
          //console.log("playVideo ok")
        })
        .catch((error) => {
          //console.log("playVideo error", error)
        })
    }
  }

  const pauseVideo = () => {
    setTimeout(() => {
      videoPlayer?.current?.pause()
      setPlaying(false)
    }, 50)
  }

  const setUnmuteState = () => {
    pauseAllVideos()
    videoPlayer?.current?.play()
    setMuted(false)
  }

  const setMuteState = () => {
    setMuted(true)
  }

  const handleMuteIconClick = () => {
    if (muted) {
      setUnmuteState()
    } else {
      setMuteState()
    }
  }

  const handlePlayPauseClick = () => {
    // see src/utils/general.js
    if (videoPlayer?.current?.playing) {
      pauseVideo()
    } else {
      playVideo()
    }
  }

  /*
   * useEffect(() => {
   *   if (playing && false) {
   *     let intervalId = setInterval(() => {
   *       setReadyState(videoPlayer.current?.readyState)
   *       console.log(videoPlayer.current?.readyState)
   *       if (videoPlayer.current?.readyState === 4) {
   *         console.log("loaded")
   *         clearInterval(intervalId)
   *       }
   *     }, 100)
   *   }
   * }, [playing])
   */

  // NT2-21240: isolated the Video Played event to capture the initial play event
  useEffect(() => {
    if (playing) {
      const eventObj = {
        module_state: "Play",
        video_name: name,
      }
      segmentEvent("Video Played", eventObj)
    } else {
      const eventObj = {
        module_state: "Paused",
        video_name: name,
      }
      segmentEvent("Video Played", eventObj)
    }
  }, [playing, name])

  // show mute icon only after poster been loaded
  useEffect(() => {
    let poster = videoPlayer?.current?.getAttribute("poster")
    if (poster && !posterLoaded) {
      var img = new Image()
      img.src = poster
      img.onload = function () {
        if (videoPlayer?.current?.getBoundingClientRect().width) {
          setPosterLoaded(true)
        }
      }
    }
  }, [posterLoaded, videoPlayer])

  return (
    <Wrapper className="c-video-wrapper">
      <Box className="video-wrap">
        <VideoControls className={`video-overlay`}>
          {hasSound && !hideMuteIcon && posterLoaded && (
            <div
              className={`mute-unmute-wrapper ${!muted ? "active" : ""}`}
              id={`i-mute-icon-wrapper`}
              onClick={handleMuteIconClick}
              tabIndex="0"
              onKeyDown={(e) =>
                e.key === "Enter" ? handleMuteIconClick() : null
              }
              aria-label="Unmute video"
            >
              <MuteIcon className={!muted ? "hidden" : ""} />

              <UnMuteIcon className={!muted ? "" : "hidden"} />
            </div>
          )}
          <div
            className={`play-pause-wrapper`}
            onClick={handlePlayPauseClick}
            tabIndex="0"
            onKeyDown={(e) =>
              e.key === "Enter" ? handlePlayPauseClick() : null
            }
            role="button"
            aria-label="Play/pause for testimonial video"
          >
            {showPlayIcon && !playing ? (
              <div className="play-icon-svg">
                <PlayIcon />
              </div>
            ) : null}
          </div>
        </VideoControls>
        <video
          ref={videoPlayer}
          id={id}
          preload={`auto`}
          autoPlay={autoPlay}
          loop={true}
          playsInline={true}
          muted={muted}
          poster={image}
          className="video"
        >
          <source src={video} type="video/mp4" />
        </video>
      </Box>
    </Wrapper>
  )
}

export default VideoTile
